// Component: ImageUploader
// Purpose: Drag-and-drop image uploader with preview. Supports existing image
// state for edit flows. Notifies parent via onImageSelected/onImageChange.
import React, { useState, useRef, useEffect } from 'react';

const ImageUploader = ({ onImageChange, onImageSelected, error, initialImage, existingImage }) => {
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState(initialImage?.name || existingImage?.name || '');
  const fileInputRef = useRef(null);

  // Set initial preview from prop if available
  useEffect(() => {
    // Handle both initialImage and existingImage for backward compatibility
    const imageSource = initialImage?.url || existingImage?.url;
    if (imageSource) {
      setPreview(imageSource);
      
      // If no file is selected yet but we have an initial image, we need to tell the parent
      // that we're using this image (for validation purposes)
      // Support both callback patterns (onImageSelected and onImageChange)
      const imageData = {
        isExistingImage: true,
        url: imageSource,
        name: initialImage?.name || existingImage?.name || 'Existing Image'
      };
      
      if (typeof onImageSelected === 'function') {
        onImageSelected(imageData);
      }
      
      if (typeof onImageChange === 'function') {
        onImageChange(imageData);
      }
    }
  }, [initialImage, existingImage, onImageSelected, onImageChange]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    if (!file.type.match('image.*')) {
      alert('Please select an image file');
      return;
    }

    setFileName(file.name);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);
    
    // Support both callback patterns
    if (typeof onImageSelected === 'function') {
      onImageSelected(file);
    }
    
    if (typeof onImageChange === 'function') {
      onImageChange(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setPreview(null);
    setFileName('');
    
    // Support both callback patterns
    if (typeof onImageSelected === 'function') {
      onImageSelected(null);
    }
    
    if (typeof onImageChange === 'function') {
      onImageChange(null);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : error
            ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
            : 'border-gray-300 hover:border-primary-500 dark:border-gray-600 dark:hover:border-primary-500'
        }`}
      >
        {preview ? (
          <div className="relative">
            <div className="absolute top-0 right-0 p-1">
              <button
                onClick={handleRemoveImage}
                className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md transition-colors"
                title="Remove image"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <img
              src={preview}
              alt="Preview"
              className="max-h-64 mx-auto object-contain rounded"
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 truncate max-w-full">
              {fileName || 'Current Image'}
            </p>
          </div>
        ) : (
          <div className="py-8">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Click to upload or drag and drop
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
              PNG, JPG, GIF up to 10MB
            </p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      {error && !preview && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default ImageUploader; 